import { Component, ErrorBoundary, JSX } from "solid-js";
import { Button } from "../ui";
import { ApiError } from "../../api/client";

export interface RouteErrorBoundaryProps {
  children: JSX.Element;
}

/**
 * Per-route error boundary.
 *
 * The app-root boundary in `App.tsx` replaces the whole viewport, so any
 * render-time throw — including one from a concern unrelated to the current
 * page — costs the user access to every other surface. Wrapping each routed
 * page in its own boundary keeps the blast radius at the page: the shell,
 * sidebar and nav stay mounted and usable, and "Try again" re-renders only
 * the failed page rather than the entire app.
 *
 * Navigating to another route mounts a different route component, which
 * disposes this boundary and its error state — so nav is always a way out.
 */
export const RouteErrorBoundary: Component<RouteErrorBoundaryProps> = (
  props,
) => {
  return (
    <ErrorBoundary
      fallback={(err, reset) => {
        // Deliberately not "Unhandled render error:" — that string means the
        // app-root boundary fired and the whole app was replaced. A contained
        // page failure is a different, lesser event and must read as one.
        console.error("Route render error:", err);
        const isRateLimited = err instanceof ApiError && err.status === 429;
        return (
          <div
            data-testid="route-error-fallback"
            class="flex h-full items-center justify-center p-8"
          >
            <div class="w-full max-w-md rounded border border-line bg-surface p-6 text-center">
              <h2 class="mb-2 text-base font-semibold text-ink">
                {isRateLimited ? "Too many requests" : "This page didn't load"}
              </h2>
              <p class="mb-5 text-sm text-ink-2">
                {isRateLimited
                  ? "You're making requests faster than the server allows. Wait a few seconds, then try again."
                  : "Something went wrong rendering this page. The rest of the app is still available."}
              </p>
              <Button onClick={reset}>Try again</Button>
            </div>
          </div>
        );
      }}
    >
      {props.children}
    </ErrorBoundary>
  );
};
